import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Building, Ownership, Party, QuotePartSchedule, Unit } from '@syndiqia/registry/contracts';
import { ApiError, useApi } from './api';
import { useSyndicate } from './session';

// One hook per Registry resource. Query keys start with the tenant id so syndicates never share cache.

export function useBuildings() {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({ queryKey: [s.tenantId, 'buildings'], queryFn: () => api<Building[]>(`${s.api}/registry/buildings`) });
}

export function useUnits() {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({ queryKey: [s.tenantId, 'units'], queryFn: () => api<Unit[]>(`${s.api}/registry/units`) });
}

export function useParties() {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'parties'],
    queryFn: () => api<Party[]>(`${s.api}/registry/parties`),
    enabled: s.can('registry.contacts.read'),
  });
}

export function useOwnership(unitId: string) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'ownership', unitId],
    queryFn: () => api<Ownership[]>(`${s.api}/registry/units/${unitId}/ownership`),
    enabled: s.can('registry.contacts.read'),
  });
}

export function useCurrentQuoteParts() {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'quote-parts', 'current'],
    queryFn: async () => {
      try {
        return await api<QuotePartSchedule>(`${s.api}/registry/quote-parts`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null; // none in force yet
        throw e;
      }
    },
  });
}

export function useQuotePartSchedules() {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'quote-parts', 'history'],
    queryFn: () => api<QuotePartSchedule[]>(`${s.api}/registry/quote-part-schedules`),
  });
}

/** A write to the Registry API that refreshes the listed resources once it succeeds. */
export function useRegistryMutation<Input, Output>(
  request: (api: ReturnType<typeof useApi>, base: string, input: Input) => Promise<Output>,
  invalidates: string[],
) {
  const api = useApi();
  const s = useSyndicate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Input) => request(api, `${s.api}/registry`, input),
    onSuccess: () =>
      Promise.all(invalidates.map((key) => queryClient.invalidateQueries({ queryKey: [s.tenantId, key] }))),
  });
}
